import { useMemo, useState } from "react";
import { Instagram, Grid3x3, Film, UserSquare2, Play, Images, Eye, Heart, MessageCircle, Bookmark, Share2, CheckCircle2, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COVER_URL = (fid) => fid ? `${API}/rivoluzione/cover/${fid}` : null;

/**
 * Anteprima realistica della pagina Instagram @colucci_assicuratori.
 * Mostra: header profilo, statistiche, bio e griglia 3-col dei contenuti con cover.
 * Props:
 *  - status: { connected, ig_username, ig_profile_picture_url, page_name }
 *  - contents: lista rivoluzione_contenuti (id, titolo, tipo, cover_file_id, link_instagram, pinned)
 *  - publications: storico pubblicazioni API (id, media_id, image_urls, caption, status)
 */
export default function InstagramProfilePreview({ status, contents = [], publications = [] }) {
  const [tab, setTab] = useState("posts");
  const [selected, setSelected] = useState(null);

  const username = status?.connected ? status.ig_username : "colucci_assicuratori";
  const avatarUrl = status?.connected ? status.ig_profile_picture_url : null;

  // Costruisci griglia combinando publications (come caroselli pubblicati) + contents
  const gridItems = useMemo(() => {
    const items = [];
    // Publications pubblicate su IG
    (publications || []).filter(p => p.status === "published").forEach(p => {
      items.push({
        id: `pub-${p.media_id || p.id}`,
        kind: "carousel",
        cover: (p.image_urls || [])[0],
        cover_count: (p.image_urls || []).length,
        caption: p.caption || "",
        timestamp: p.published_at,
        ig_link: p.media_id ? `https://www.instagram.com/p/${p.media_id}/` : null,
        pinned: false,
      });
    });
    // Contenuti redazione (reel / carousel) con cover
    (contents || []).filter(c => c.cover_file_id && c.attivo !== false).forEach(c => {
      items.push({
        id: `c-${c.id}`,
        kind: c.tipo === "reel" ? "reel" : "carousel",
        cover: COVER_URL(c.cover_file_id),
        cover_count: c.tipo === "reel" ? null : 1,
        caption: c.titolo || "",
        timestamp: c.updated_at || c.created_at,
        ig_link: c.link_instagram || null,
        pinned: !!c.pinned,
      });
    });
    // Ordina: pinned prima, poi timestamp desc
    items.sort((a, b) => {
      if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
      return (b.timestamp || "").localeCompare(a.timestamp || "");
    });
    return items;
  }, [contents, publications]);

  const postsCount = gridItems.length;
  const reelsItems = gridItems.filter(i => i.kind === "reel");

  const displayItems = tab === "reels" ? reelsItems : gridItems;

  return (
    <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden" data-testid="ig-profile-preview">
      {/* Header preview */}
      <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-zinc-100 bg-gradient-to-r from-[#FEDA77]/5 via-[#F58529]/5 to-[#DD2A7B]/5">
        <div className="flex items-center gap-2">
          <Instagram className="w-4 h-4 text-[#DD2A7B]" />
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Anteprima Profilo Instagram</p>
          {!status?.connected && (
            <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[9px] font-semibold border border-amber-200">Simulazione</span>
          )}
          {status?.connected && (
            <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-[9px] font-semibold border border-green-200 flex items-center gap-1">
              <CheckCircle2 className="w-2.5 h-2.5" /> Live
            </span>
          )}
        </div>
        <a href={`https://www.instagram.com/${username}/`} target="_blank" rel="noopener noreferrer"
          className="text-[11px] font-medium text-zinc-500 hover:text-[#DD2A7B] flex items-center gap-1" data-testid="ig-preview-open-external">
          Apri su Instagram <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      {/* Profile info */}
      <div className="px-6 py-6">
        <div className="flex items-start gap-6 flex-wrap sm:flex-nowrap">
          {/* Avatar con gradient IG */}
          <div className="shrink-0">
            <div className="p-[3px] rounded-full bg-gradient-to-tr from-[#FEDA77] via-[#F58529] to-[#DD2A7B]">
              <div className="bg-white p-[3px] rounded-full">
                {avatarUrl ? (
                  <img src={avatarUrl} alt={username} className="w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover" />
                ) : (
                  <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-[#0F1F14] flex items-center justify-center">
                    <span className="font-heading text-3xl font-bold text-[#D97B58]">C</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Username + stats */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap mb-3">
              <p className="text-xl font-medium text-zinc-900">{username}</p>
              <span className="inline-flex items-center gap-1 text-xs font-medium bg-[#0F1F14] text-white px-2 py-1 rounded-full">
                <CheckCircle2 className="w-3 h-3 text-[#D97B58]" /> Professionale
              </span>
              <button className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#0F1F14] text-white hover:bg-[#1d4a2e] transition">Segui</button>
              <button className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-zinc-100 text-zinc-900 hover:bg-zinc-200 transition">Messaggio</button>
            </div>
            <div className="flex items-center gap-6 text-sm text-zinc-700 mb-3">
              <span><strong className="text-zinc-900">{postsCount}</strong> post</span>
              <span><strong className="text-zinc-900">—</strong> follower</span>
              <span><strong className="text-zinc-900">—</strong> seguiti</span>
            </div>
            <div className="text-sm text-zinc-800 leading-relaxed">
              <p className="font-semibold">Colucci Assicuratori</p>
              <p className="text-zinc-600">Agenzia assicurativa indipendente · IVASS iscritti</p>
              <p className="text-zinc-600">News quotidiane in carosello · Video-pillole assicurative</p>
              <p className="text-[#D97B58] font-medium mt-0.5">colucciassicuratori.com</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-t border-zinc-200 flex items-center justify-center gap-10 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-400">
        {[
          { id: "posts", label: "Post", icon: Grid3x3 },
          { id: "reels", label: "Reel", icon: Film },
          { id: "tagged", label: "Tag", icon: UserSquare2 },
        ].map(t => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`relative py-3.5 flex items-center gap-2 transition-colors ${active ? "text-zinc-900" : "hover:text-zinc-600"}`}
              data-testid={`ig-preview-tab-${t.id}`}>
              {active && <span className="absolute top-0 left-0 right-0 h-[2px] bg-zinc-900" />}
              <Icon className="w-3.5 h-3.5" />
              {t.label}
              {t.id === "reels" && reelsItems.length > 0 && <span className="text-zinc-400 font-normal">({reelsItems.length})</span>}
            </button>
          );
        })}
      </div>

      {/* Grid */}
      {displayItems.length === 0 ? (
        <div className="py-16 text-center" data-testid="ig-preview-empty">
          <div className="w-14 h-14 rounded-full border-2 border-zinc-200 mx-auto mb-3 flex items-center justify-center">
            <Instagram className="w-6 h-6 text-zinc-300" />
          </div>
          <p className="text-sm font-semibold text-zinc-700">Nessun post ancora</p>
          <p className="text-xs text-zinc-400 mt-1">Aggiungi contenuti o pubblica un carosello per vederli qui.</p>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-[2px] bg-zinc-100 p-[2px]" data-testid="ig-preview-grid">
          {displayItems.map(item => (
            <button key={item.id} onClick={() => setSelected(item)}
              className="group relative aspect-square overflow-hidden bg-zinc-200"
              data-testid={`ig-preview-tile-${item.id}`}>
              {item.cover ? (
                <img src={item.cover} alt={item.caption} className="absolute inset-0 w-full h-full object-cover transition-transform group-hover:scale-[1.02]" loading="lazy" />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-[#0F1F14] to-[#1d4a2e] flex items-center justify-center">
                  <Instagram className="w-8 h-8 text-[#D97B58]/40" />
                </div>
              )}
              {/* Tipo indicator */}
              <div className="absolute top-1.5 right-1.5 text-white drop-shadow-lg">
                {item.kind === "reel" ? <Play className="w-4 h-4 fill-white" /> : <Images className="w-4 h-4" />}
              </div>
              {item.pinned && (
                <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded bg-black/60 text-white text-[8px] font-bold uppercase tracking-wider">Fissato</div>
              )}
              {/* Hover stats (simulazione) */}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3 text-white font-semibold text-xs">
                <span className="flex items-center gap-1"><Heart className="w-3.5 h-3.5 fill-white" /> —</span>
                <span className="flex items-center gap-1"><MessageCircle className="w-3.5 h-3.5 fill-white" /> —</span>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Modal preview singolo post */}
      {selected && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" onClick={() => setSelected(null)} data-testid="ig-preview-modal">
          <div className="bg-white rounded-xl overflow-hidden max-w-lg w-full max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 px-4 py-3 border-b border-zinc-100">
              <div className="p-[2px] rounded-full bg-gradient-to-tr from-[#FEDA77] via-[#F58529] to-[#DD2A7B]">
                <div className="bg-white p-[2px] rounded-full">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt="" className="w-8 h-8 rounded-full object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-[#0F1F14] flex items-center justify-center text-[#D97B58] text-xs font-bold">C</div>
                  )}
                </div>
              </div>
              <p className="text-sm font-semibold text-zinc-900 flex-1">{username}</p>
              <button onClick={() => setSelected(null)} className="text-zinc-400 hover:text-zinc-700 text-2xl leading-none">&times;</button>
            </div>
            {selected.cover && (
              <div className="aspect-square bg-zinc-900 relative">
                <img src={selected.cover} alt="" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute top-3 right-3 text-white drop-shadow-lg">
                  {selected.kind === "reel" ? <Play className="w-5 h-5 fill-white" /> : <Images className="w-5 h-5" />}
                </div>
              </div>
            )}
            <div className="px-4 py-3 border-t border-zinc-100">
              <div className="flex items-center gap-4 mb-2 text-zinc-700">
                <Heart className="w-6 h-6" />
                <MessageCircle className="w-6 h-6" />
                <Share2 className="w-5 h-5" />
                <Bookmark className="w-6 h-6 ml-auto" />
              </div>
              <p className="text-xs text-zinc-500 mb-1 flex items-center gap-1">
                <Eye className="w-3 h-3" /> Anteprima — metriche simulate
              </p>
              {selected.caption && <p className="text-sm text-zinc-800 line-clamp-4"><strong>{username}</strong> {selected.caption}</p>}
              {selected.ig_link && (
                <a href={selected.ig_link} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-semibold text-[#DD2A7B] hover:underline mt-2">
                  Apri post su Instagram <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
